import React from "react";
import { Col, Row } from "@insightfulscience/atomic-react/Grid";

import Link from "@insightfulscience/atomic-react/Link";
import Hero from "@insightfulscience/atomic-react/Hero";
import HeroDescription from "@insightfulscience/atomic-react/Hero/Description";
import HeroAction from "@insightfulscience/atomic-react/Hero/Action";
import { HeroCol, HeroRow } from "@insightfulscience/atomic-react/Hero/Grid";
import HeroButtonLink from "@insightfulscience/atomic-react/Hero/ButtonLink";
import HeroBackground from "@insightfulscience/atomic-react/Hero/HeroBackground";
import FeatureMeta from "@insightfulscience/atomic-react/Feature/Meta";
import FeatureImage from "@insightfulscience/atomic-react/Feature/Image";
import Feature from "@insightfulscience/atomic-react/Feature";
import LandingSimpleSection from "@insightfulscience/atomic-react/r2/LandingElement/SimpleSection";
import LandingSimpleSectionStripped from "@insightfulscience/atomic-react/LandingElement/SimpleSectionStripped";

import Paragraph from "@insightfulscience/atomic-react/Paragraph";

import UList from "@insightfulscience/atomic-react/UList";
import ContentContainer from "@insightfulscience/atomic-react/r1/atoms/ContentContainer";
import PageNavbar from "@insightfulscience/atomic-react/PageNavbar";
import PageNavbarItem from "@insightfulscience/atomic-react/PageNavbar/Item";
import PageNavbarLink from "@insightfulscience/atomic-react/PageNavbar/Link";

import WhatIsNew from "@insightfulscience/atomic-react/WhatIsNew";
import WhatIsNewImage from "@insightfulscience/atomic-react/WhatIsNew/Image";
import WhatIsNewContent from "@insightfulscience/atomic-react/WhatIsNew/Content";
import Heading from "@insightfulscience/atomic-react/Heading";
import Icon from "@insightfulscience/atomic-react/Icon";
import Line from "@insightfulscience/atomic-react/Line";
import { useLocale } from "@insightfulscience/smart-react/i18";
import PageMeta from "@insightfulscience/smart-react/PageMeta";
import Image from "@insightfulscience/atomic-react/Image";
import Html from "@insightfulscience/smart-react/Html";
import pipe from "@insightfulscience/shared-utils/fn/pipe";
import SiteLayout from "../../SiteLayout";

import WithServices from "../../services";
import SectionStart30DayTrial from "../../SectionStart30DayTrial";
import AvailableOnOs from "../../AvailableOnOs";
import withErrorHandling from "../../AppError/withErrorHandling";

const PrismAnova = () => {
	const [t] = useLocale("prism");
	return (
		<SiteLayout>
			<PageMeta title={t("META.TITLE")} description={t("META.DESCRIPTION")}>
				<PageMeta.Canonical href="https://www.graphpad.com/features" />
			</PageMeta>
			<Hero color="gradient" align="left">
				<Row>
					<Col lg={10} lgOffset={2}>
						<Row>
							<Col>
								<Image
									src="/images/graphpad/prism/logo-prism-white.svg"
									className="ml-m32--lg h-16"
								/>
							</Col>
							<Col lg={8}>
								<HeroDescription sx={{ fontColor: "black--10" }} spacing={{ mt: 5 }}>
									{t("HERO_DESCRIPTION")}
								</HeroDescription>
								<HeroAction center={false}>
									<HeroRow>
										<HeroCol>
											<HeroButtonLink href="/demos/" isBlock>
												{t("START_A_FREE_TRIAL")}
											</HeroButtonLink>
										</HeroCol>
										<HeroCol>
											<HeroButtonLink href="/how-to-buy/" styleType="info-light" isOutline2 isBlock>
												{t("PRICING")}
											</HeroButtonLink>
										</HeroCol>
									</HeroRow>
								</HeroAction>
							</Col>
						</Row>
					</Col>
				</Row>

				<HeroBackground>
					<Image src="/images/graphpad/prism/hero-bg.png" />
				</HeroBackground>
			</Hero>
			<PageNavbar bgColor="black--10">
				<PageNavbarItem>
					<PageNavbarLink href="/updates">
						{t("DISCOVER")} <Icon name="angle-right" spacing={{ ml: 1 }} />
					</PageNavbarLink>
				</PageNavbarItem>
			</PageNavbar>
			<LandingSimpleSectionStripped>
				<LandingSimpleSection.TitleV2 spacing={{ mb: 20 }}>
					{t("COMPREHENSIVE_ANALYSIS")}
				</LandingSimpleSection.TitleV2>
				<LandingSimpleSection.Body>
					<Feature>
						<FeatureMeta md={6}>
							<FeatureMeta.Title>{t("ORGANIZE_YOUR_DATA")}</FeatureMeta.Title>
							<FeatureMeta.Description>
								<Paragraph>{t("ORGANIZE_YOUR_DATA_DESC")}</Paragraph>
							</FeatureMeta.Description>
							<FeatureMeta.Action>
								<Link sx={{ fontSize: "m" }} href="/demos/">
									{t("START_A_FREE_TRIAL")}
								</Link>
							</FeatureMeta.Action>
						</FeatureMeta>
						<FeatureImage src="/images/graphpad/prism/prism-multi-variables-w1182-800.png" md={6} />
					</Feature>
					<Feature>
						<FeatureImage src="/images/graphpad/prism/prism-t-test-w1076-800.png" md={6} />
						<FeatureMeta md={6}>
							<FeatureMeta.Title>{t("PERFORM_THE_RIGHT_ANALYSIS")}</FeatureMeta.Title>
							<FeatureMeta.Description>
								<Paragraph>
									<Html Tag="span">
										{t("PERFORM_THE_RIGHT_ANALYSIS_DESC", {
											anovaLink: "/features/prism-anova",
											nonlinearLink: "/features/prism-nonlinear-regression",
											link: "/features/prism-survival-analysis",
										})}
									</Html>
								</Paragraph>
							</FeatureMeta.Description>
							<FeatureMeta.Action>
								<Link sx={{ fontSize: "m" }} href="/demos/">
									{t("START_A_FREE_TRIAL")}
								</Link>
							</FeatureMeta.Action>
						</FeatureMeta>
					</Feature>
					<Feature>
						<FeatureMeta md={6}>
							<FeatureMeta.Title>{t("GET_ACTIONABLE_HELP")}</FeatureMeta.Title>
							<FeatureMeta.Description>
								<Paragraph>
									<Html Tag="span">
										{t("GET_ACTIONABLE_HELP_DESC", {
											link: "/prism-academy",
										})}
									</Html>
								</Paragraph>
							</FeatureMeta.Description>
							<FeatureMeta.Action>
								<Link sx={{ fontSize: "m" }} href="/demos/">
									{t("START_A_FREE_TRIAL")}
								</Link>
							</FeatureMeta.Action>
						</FeatureMeta>
						<FeatureImage src="/images/graphpad/prism/prism-welcome-dialog-w1059-800.png" md={6} />
					</Feature>
				</LandingSimpleSection.Body>
			</LandingSimpleSectionStripped>
			<LandingSimpleSectionStripped isDark>
				<LandingSimpleSection.TitleV2 spacing={{ mb: 20 }}>
					{t("WORK_SMARTER_NOT_HARDER")}
				</LandingSimpleSection.TitleV2>
				<LandingSimpleSection.Body>
					<Feature>
						<FeatureImage src="/images/graphpad/prism/prism-michaelis-w1102-800.png" md={6} />

						<FeatureMeta md={6}>
							<FeatureMeta.Title>{t("ONE_CLICK_REGRESSION")}</FeatureMeta.Title>
							<FeatureMeta.Description>
								<Paragraph>{t("ONE_CLICK_REGRESSION_DESC")}</Paragraph>
							</FeatureMeta.Description>
							<FeatureMeta.Action>
								<Link sx={{ fontSize: "m" }} href="/demos/">
									{t("START_A_FREE_TRIAL")}
								</Link>
							</FeatureMeta.Action>
						</FeatureMeta>
					</Feature>
					<Feature>
						<FeatureMeta md={6}>
							<FeatureMeta.Title>{t("FOCUS_ON_YOUR_RESEARCH")}</FeatureMeta.Title>
							<FeatureMeta.Description>
								<Paragraph>{t("FOCUS_ON_YOUR_RESEARCH_DESC")}</Paragraph>
							</FeatureMeta.Description>
							<FeatureMeta.Action>
								<Link sx={{ fontSize: "m" }} href="/demos/">
									{t("START_A_FREE_TRIAL")}
								</Link>
							</FeatureMeta.Action>
						</FeatureMeta>
						<FeatureImage src="/images/graphpad/prism/prism-xy-w1224-800.png" md={6} />
					</Feature>
					<Feature>
						<FeatureImage
							src="/images/graphpad/prism/prism-stars-on-graph-options-w1210-800.png"
							md={6}
						/>

						<FeatureMeta md={6}>
							<FeatureMeta.Title>{t("AUTOMATE_YOUR_WORK")}</FeatureMeta.Title>
							<FeatureMeta.Description>
								<Paragraph>{t("AUTOMATE_YOUR_WORK_DESC")}</Paragraph>
							</FeatureMeta.Description>
							<FeatureMeta.Action>
								<Link sx={{ fontSize: "m" }} href="/demos/">
									{t("START_A_FREE_TRIAL")}
								</Link>
							</FeatureMeta.Action>
						</FeatureMeta>
					</Feature>
				</LandingSimpleSection.Body>
			</LandingSimpleSectionStripped>
			<LandingSimpleSectionStripped>
				<LandingSimpleSection.TitleV2 spacing={{ mb: 20 }}>
					{t("THE_FASTEST_WAY")}
				</LandingSimpleSection.TitleV2>
				<LandingSimpleSection.Body>
					<Feature>
						<FeatureMeta md={6}>
							<FeatureMeta.Title>{t("COUNTLESS_WAYS")}</FeatureMeta.Title>
							<FeatureMeta.Description>
								<Paragraph>{t("COUNTLESS_WAYS_DESC")}</Paragraph>
							</FeatureMeta.Description>
							<FeatureMeta.Action>
								<Link sx={{ fontSize: "m" }} href="/demos/">
									{t("START_A_FREE_TRIAL")}
								</Link>
							</FeatureMeta.Action>
						</FeatureMeta>
						<FeatureImage src="/images/graphpad/prism/prism-customize-w1252-800.png" md={6} />
					</Feature>
					<Feature>
						<FeatureImage src="/images/graphpad/prism/data-graph-inspector.png" md={6} />

						<FeatureMeta md={6}>
							<FeatureMeta.Title>{t("EXPLORE_YOUR_DATA")}</FeatureMeta.Title>
							<FeatureMeta.Description>
								<Paragraph>{t("EXPLORE_YOUR_DATA_DESC")}</Paragraph>
							</FeatureMeta.Description>
							<FeatureMeta.Action>
								<Link sx={{ fontSize: "m" }} href="/demos/">
									{t("START_A_FREE_TRIAL")}
								</Link>
							</FeatureMeta.Action>
						</FeatureMeta>
					</Feature>
					<Feature>
						<FeatureMeta md={6}>
							<FeatureMeta.Title>{t("EXPORT_PUBLICATION_QUALITY")}</FeatureMeta.Title>
							<FeatureMeta.Description>
								<Paragraph>{t("EXPORT_PUBLICATION_QUALITY_DESC")}</Paragraph>
							</FeatureMeta.Description>
							<FeatureMeta.Action>
								<Link sx={{ fontSize: "m" }} href="/demos/">
									{t("START_A_FREE_TRIAL")}
								</Link>
							</FeatureMeta.Action>
						</FeatureMeta>
						<FeatureImage src="/images/graphpad/prism/prism-publication-w1178-800.png" md={6} />
					</Feature>
					<Feature>
						<FeatureImage src="/images/graphpad/cloud/discuss-upd.png" md={6} />

						<FeatureMeta md={6}>
							<FeatureMeta.Title>{t("ENHANCE_COLLABORATION")}</FeatureMeta.Title>
							<FeatureMeta.Description>
								<Paragraph>{t("ENHANCE_COLLABORATION_DESC")}</Paragraph>
							</FeatureMeta.Description>
							<FeatureMeta.Action>
								<Link sx={{ fontSize: "m" }} href="/demos/">
									{t("START_A_FREE_TRIAL")}
								</Link>
							</FeatureMeta.Action>
						</FeatureMeta>
					</Feature>
				</LandingSimpleSection.Body>
			</LandingSimpleSectionStripped>
			<LandingSimpleSectionStripped utility={{ bgColor: "5" }}>
				<LandingSimpleSection.TitleV2 sx={{ fontColor: "white" }} spacing={{ mb: 5 }}>
					{t("DISCOVER_WHATS_NEW")}
				</LandingSimpleSection.TitleV2>
				<ContentContainer center lg={8}>
					<LandingSimpleSection.DescriptionV2 sx={{ fontColor: "white" }}>
						{t("INTRODUCING_AN_EVEN")}
					</LandingSimpleSection.DescriptionV2>
				</ContentContainer>

				<ContentContainer center lg={9}>
					<Line isGradient spacing={{ mb: 10 }} />
					<Heading
						Tag="h3"
						sx={{ fontSize: "l", fontColor: "white", textAlign: "center" }}
						spacing={{ my: 15 }}
					>
						{t("ENHANCED_DATA_VISUALIZATION")}
					</Heading>
					<WhatIsNew>
						<WhatIsNewImage src="/images/graphpad/prism/prism-bubble-w350-200.png" />
						<WhatIsNewContent>
							<WhatIsNewContent.Title>{t("BUBBLE_PLOTS")}</WhatIsNewContent.Title>
							<WhatIsNewContent.Description>{t("BUBBLE_PLOTS_DESC")}</WhatIsNewContent.Description>
						</WhatIsNewContent>
					</WhatIsNew>
					<WhatIsNew>
						<WhatIsNewImage src="/images/graphpad/prism/prism-violin-w350-200.png" />
						<WhatIsNewContent>
							<WhatIsNewContent.Title>{t("VIOLIN_PLOTS")}</WhatIsNewContent.Title>
							<WhatIsNewContent.Description>{t("VIOLIN_PLOTS_DESC")}</WhatIsNewContent.Description>
						</WhatIsNewContent>
					</WhatIsNew>
					<WhatIsNew>
						<WhatIsNewImage src="/images/graphpad/prism/prism-estimationplots-w350-200.png" />
						<WhatIsNewContent>
							<WhatIsNewContent.Title>{t("ESTIMATION_PLOTS")}</WhatIsNewContent.Title>
							<WhatIsNewContent.Description>
								{t("ESTIMATION_PLOTS_DESC")}
							</WhatIsNewContent.Description>
						</WhatIsNewContent>
					</WhatIsNew>
					<WhatIsNew>
						<WhatIsNewImage src="/images/graphpad/prism/prism-wn-smoothing-spline-w1281-200.png" />
						<WhatIsNewContent>
							<WhatIsNewContent.Title>{t("SMOOTHING_SPLINE")}</WhatIsNewContent.Title>
							<WhatIsNewContent.Description>
								{t("SMOOTHING_SPLINE_DESC")}
							</WhatIsNewContent.Description>
						</WhatIsNewContent>
					</WhatIsNew>
					<Row>
						<Col sx={{ textAlign: "center" }} spacing={{ my: 10 }}>
							<Link
								sx={{
									fontSize: "xl",
									fontWeight: "medium",
									fontColor: "4",
									hover: { fontColor: "white" },
								}}
								href="/guides/prism/latest/user-guide/whats-new-highlights.htm"
							>
								{t("READ_MORE")}
							</Link>
						</Col>
					</Row>
				</ContentContainer>
				<ContentContainer center lg={9}>
					<LandingSimpleSection.Body>
						<Line isGradient spacing={{ mb: 10 }} />
						<Heading
							Tag="h3"
							sx={{ fontSize: "l", fontColor: "white", textAlign: "center" }}
							spacing={{ my: 15 }}
						>
							{t("IMPROVED_GRAPHING_AND_CUSTOMIZATION_OPTIONS")}
						</Heading>
						<WhatIsNew>
							<WhatIsNewImage src="/images/graphpad/prism/stars-on-graph.jpg" />
							<WhatIsNewContent>
								<WhatIsNewContent.Title>{t("STARS_ON_GRAPH")}</WhatIsNewContent.Title>
								<WhatIsNewContent.Description>
									{t("STARS_ON_GRAPH_DESC")}
								</WhatIsNewContent.Description>
							</WhatIsNewContent>
						</WhatIsNew>
						<WhatIsNew>
							<WhatIsNewImage src="/images/graphpad/prism/improved-graph-customization.png" />
							<WhatIsNewContent>
								<WhatIsNewContent.Title>{t("IMPROVED_GRAPH_CUSTOMIZATION")}</WhatIsNewContent.Title>
								<WhatIsNewContent.Description>
									{t("IMPROVED_GRAPH_CUSTOMIZATION_DESC")}
								</WhatIsNewContent.Description>
							</WhatIsNewContent>
						</WhatIsNew>
						<WhatIsNew>
							<WhatIsNewImage src="/images/graphpad/prism/prism-wn-automatically-label-bar-graphs-w1281-200.png" />
							<WhatIsNewContent>
								<WhatIsNewContent.Title>
									{t("AUTOMATICALLY_LABEL_BAR_GRAPHS")}
								</WhatIsNewContent.Title>
								<WhatIsNewContent.Description>
									<Html Tag="span">{t("AUTOMATICALLY_LABEL_BAR_GRAPHS_DESC")}</Html>
								</WhatIsNewContent.Description>
							</WhatIsNewContent>
						</WhatIsNew>
						<WhatIsNew>
							<WhatIsNewImage src="/images/graphpad/prism/prism-wn-improved-group-graphs-w960-200.png" />
							<WhatIsNewContent>
								<WhatIsNewContent.Title>{t("IMPROVED_GROUPED_GRAPHS")}</WhatIsNewContent.Title>
								<WhatIsNewContent.Description>
									{t("IMPROVED_GROUPED_GRAPHS_DESC")}
								</WhatIsNewContent.Description>
							</WhatIsNewContent>
						</WhatIsNew>
						<Row>
							<Col sx={{ textAlign: "center" }} spacing={{ my: 10 }}>
								<Link
									sx={{
										fontSize: "xl",
										fontWeight: "medium",
										fontColor: "4",
										hover: { fontColor: "white" },
									}}
									href="/guides/prism/latest/user-guide/better-looking-graphs.htm"
								>
									{t("READ_MORE")}
								</Link>
							</Col>
						</Row>
					</LandingSimpleSection.Body>
				</ContentContainer>
				<ContentContainer center lg={9}>
					<Line isGradient spacing={{ mb: 10 }} />
					<Row utility={{ justify: "center" }}>
						<Col md={10}>
							<Heading Tag="h3" sx={{ fontSize: "l", fontColor: "white", textAlign: "center" }}>
								{t("MORE_INTUITIVE_NAVIGATION")}
							</Heading>
							<Row utility={{ justify: "center" }}>
								<Col lg={10}>
									<Heading
										Tag="h3"
										sx={{
											fontSize: "m",
											fontWeight: "medium",
											fontColor: "white",
											textAlign: "center",
										}}
										spacing={{ mt: 10 }}
									>
										{t("GREATER_COLLABORATION")}
									</Heading>
									<Paragraph sx={{ fontColor: "black--15", textAlign: "center" }}>
										{t("GREATER_COLLABORATION_DESC")}
									</Paragraph>
								</Col>
							</Row>
							<Row utility={{ justify: "center" }}>
								<Col lg={10}>
									<Heading
										Tag="h3"
										sx={{
											fontSize: "m",
											fontWeight: "medium",
											fontColor: "white",
											textAlign: "center",
										}}
										spacing={{ mt: 10 }}
									>
										{t("FAIR_DATA")}
									</Heading>
									<Paragraph sx={{ fontColor: "black--15", textAlign: "center" }}>
										{t("FAIR_DATA_DESC")}
									</Paragraph>
								</Col>
							</Row>
							<Row utility={{ justify: "center" }}>
								<Col lg={10}>
									<Heading
										Tag="h3"
										sx={{
											fontSize: "m",
											fontWeight: "medium",
											fontColor: "white",
											textAlign: "center",
										}}
										spacing={{ mt: 10 }}
									>
										{t("EXPANDED_DATA")}
									</Heading>
									<Paragraph sx={{ fontColor: "black--15", textAlign: "center" }}>
										{t("EXPANDED_DATA_DESC")}
									</Paragraph>
								</Col>
							</Row>
							<Row utility={{ justify: "center" }}>
								<Col lg={10}>
									<Heading
										Tag="h3"
										sx={{
											fontSize: "m",
											fontWeight: "medium",
											fontColor: "white",
											textAlign: "center",
										}}
										spacing={{ mt: 10 }}
									>
										{t("INTELLIGENT_DATA_WRANGLING")}
									</Heading>
									<Paragraph sx={{ fontColor: "black--15", textAlign: "center" }}>
										{t("INTELLIGENT_DATA_WRANGLING_DESC")}
									</Paragraph>
								</Col>
							</Row>
							<Row utility={{ justify: "center" }}>
								<Col lg={10}>
									<Heading
										Tag="h3"
										sx={{
											fontSize: "m",
											fontWeight: "medium",
											fontColor: "white",
											textAlign: "center",
										}}
										spacing={{ mt: 10 }}
									>
										{t("HOOK_CONSTANT")}
									</Heading>
									<Paragraph sx={{ fontColor: "black--15", textAlign: "center" }}>
										{t("HOOK_CONSTANT_DESC")}
									</Paragraph>
								</Col>
							</Row>
							<Row>
								<Col sx={{ textAlign: "center" }} spacing={{ my: 10 }}>
									<Link
										sx={{
											fontSize: "xl",
											fontWeight: "medium",
											fontColor: "4",
											hover: { fontColor: "white" },
										}}
										href="/updates/prism-1000-release-notes"
									>
										{t("READ_MORE")}
									</Link>
								</Col>
							</Row>
						</Col>
					</Row>
				</ContentContainer>
				<ContentContainer center lg={9}>
					<Line isGradient spacing={{ mb: 10 }} />
					<Row utility={{ justify: "center" }}>
						<Col md={10}>
							<Heading Tag="h3" sx={{ fontSize: "l", fontColor: "white", textAlign: "center" }}>
								{t("NOW_FEATURING_EIGHT_KINDS_OF_DATA_TABLES")}
							</Heading>
							<Row utility={{ justify: "center" }}>
								<Col lg={10}>
									<Heading
										Tag="h3"
										sx={{
											fontSize: "m",
											fontWeight: "medium",
											fontColor: "white",
											textAlign: "center",
										}}
										spacing={{ mt: 10 }}
									>
										{t("XY_TABLES")}
									</Heading>
									<Paragraph sx={{ fontColor: "black--15", textAlign: "center" }}>
										{t("XY_TABLES_DESC")}
									</Paragraph>
								</Col>
							</Row>
							<Row utility={{ justify: "center" }}>
								<Col lg={10}>
									<Heading
										Tag="h3"
										sx={{
											fontSize: "m",
											fontWeight: "medium",
											fontColor: "white",
											textAlign: "center",
										}}
										spacing={{ mt: 10 }}
									>
										{t("COLUMN_TABLES")}
									</Heading>
									<Paragraph sx={{ fontColor: "black--15", textAlign: "center" }}>
										{t("COLUMN_TABLES_DESC")}
									</Paragraph>
								</Col>
							</Row>
							<Row utility={{ justify: "center" }}>
								<Col lg={10}>
									<Heading
										Tag="h3"
										sx={{
											fontSize: "m",
											fontWeight: "medium",
											fontColor: "white",
											textAlign: "center",
										}}
										spacing={{ mt: 10 }}
									>
										{t("GROUPED_TABLES")}
									</Heading>
									<Paragraph sx={{ fontColor: "black--15", textAlign: "center" }}>
										{t("GROUPED_TABLES_DESC")}
									</Paragraph>
								</Col>
							</Row>
							<Row utility={{ justify: "center" }}>
								<Col lg={10}>
									<Heading
										Tag="h3"
										sx={{
											fontSize: "m",
											fontWeight: "medium",
											fontColor: "white",
											textAlign: "center",
										}}
										spacing={{ mt: 10 }}
									>
										{t("CONTINGENCY_TABLES")}
									</Heading>
									<Paragraph sx={{ fontColor: "black--15", textAlign: "center" }}>
										{t("CONTINGENCY_TABLES_DESC")}
									</Paragraph>
								</Col>
							</Row>
							<Row utility={{ justify: "center" }}>
								<Col lg={10}>
									<Heading
										Tag="h3"
										sx={{
											fontSize: "m",
											fontWeight: "medium",
											fontColor: "white",
											textAlign: "center",
										}}
										spacing={{ mt: 10 }}
									>
										{t("SURVIVAL_TABLES")}
									</Heading>
									<Paragraph sx={{ fontColor: "black--15", textAlign: "center" }}>
										{t("SURVIVAL_TABLES_DESC")}
									</Paragraph>
								</Col>
							</Row>
							<Row utility={{ justify: "center" }}>
								<Col lg={10}>
									<Heading
										Tag="h3"
										sx={{
											fontSize: "m",
											fontWeight: "medium",
											fontColor: "white",
											textAlign: "center",
										}}
										spacing={{ mt: 10 }}
									>
										{t("PARTS_OF_WHOLE_TABLES")}
									</Heading>
									<Paragraph sx={{ fontColor: "black--15", textAlign: "center" }}>
										{t("PARTS_OF_WHOLE_TABLES_DESC")}
									</Paragraph>
								</Col>
							</Row>
							<Row utility={{ justify: "center" }}>
								<Col lg={10}>
									<Heading
										Tag="h3"
										sx={{
											fontSize: "m",
											fontWeight: "medium",
											fontColor: "white",
											textAlign: "center",
										}}
										spacing={{ mt: 10 }}
									>
										{t("MULTIPLE_VARIABLES_TABLES")}
									</Heading>
									<Paragraph sx={{ fontColor: "black--15", textAlign: "center" }}>
										{t("MULTIPLE_VARIABLES_TABLES_DESC")}
									</Paragraph>
								</Col>
							</Row>
							<Row utility={{ justify: "center" }}>
								<Col lg={10}>
									<Heading
										Tag="h3"
										sx={{
											fontSize: "m",
											fontWeight: "medium",
											fontColor: "white",
											textAlign: "center",
										}}
										spacing={{ mt: 10 }}
									>
										{t("NESTED_TABLES")}
									</Heading>
									<Paragraph sx={{ fontColor: "black--15", textAlign: "center" }}>
										{t("NESTED_TABLES_DESC")}
									</Paragraph>
								</Col>
							</Row>

							<Row>
								<Col sx={{ textAlign: "center" }} spacing={{ my: 10 }}>
									<Link
										sx={{
											fontSize: "xl",
											fontWeight: "medium",
											fontColor: "4",
											hover: { fontColor: "white" },
										}}
										href="/guides/prism/latest/user-guide/using_data_table_format.htm"
									>
										{t("READ_MORE")}
									</Link>
								</Col>
							</Row>
						</Col>
					</Row>
				</ContentContainer>
				<ContentContainer center lg={9}>
					<Line isGradient spacing={{ mb: 10 }} />
					<Row utility={{ justify: "center" }}>
						<Col md={10}>
							<Heading Tag="h3" sx={{ fontSize: "l", fontColor: "white", textAlign: "center" }}>
								{t("MORE_SOPHISTICATED_STATISTICAL_ANALYSES")}
							</Heading>
							<Row utility={{ justify: "center" }}>
								<Col lg={10}>
									<Heading
										Tag="h3"
										sx={{
											fontSize: "m",
											fontWeight: "medium",
											fontColor: "white",
											textAlign: "center",
										}}
										spacing={{ mt: 10 }}
									>
										{t("PERFORM_REPEATED")}
									</Heading>
									<Paragraph sx={{ fontColor: "black--15", textAlign: "center" }}>
										{t("PERFORM_REPEATED_DESC")}
									</Paragraph>
								</Col>
							</Row>
							<Row utility={{ justify: "center" }}>
								<Col lg={10}>
									<Heading
										Tag="h3"
										sx={{
											fontSize: "m",
											fontWeight: "medium",
											fontColor: "white",
											textAlign: "center",
										}}
										spacing={{ mt: 10 }}
									>
										{t("PERFORM_SIMPLE")}
									</Heading>
									<Paragraph sx={{ fontColor: "black--15", textAlign: "center" }}>
										{t("PERFORM_SIMPLE_DESC")}
									</Paragraph>
								</Col>
							</Row>
							<Row utility={{ justify: "center" }}>
								<Col lg={10}>
									<Heading
										Tag="h3"
										sx={{
											fontSize: "m",
											fontWeight: "medium",
											fontColor: "white",
											textAlign: "center",
										}}
										spacing={{ mt: 10 }}
									>
										{t("PRINCIPAL_COMPONENT_ANALYSIS")}
									</Heading>
									<Paragraph sx={{ fontColor: "black--15", textAlign: "center" }}>
										{t("PRINCIPAL_COMPONENT_ANALYSIS_DESC")}
									</Paragraph>
								</Col>
							</Row>
							<Row utility={{ justify: "center" }}>
								<Col lg={10}>
									<Heading
										Tag="h3"
										sx={{
											fontSize: "m",
											fontWeight: "medium",
											fontColor: "white",
											textAlign: "center",
										}}
										spacing={{ mt: 10 }}
									>
										{t("MULTIPLE_T_TEST")}
									</Heading>
									<Paragraph sx={{ fontColor: "black--15", textAlign: "center" }}>
										{t("MULTIPLE_T_TEST_DESC")}
									</Paragraph>
								</Col>
							</Row>
							<Row utility={{ justify: "center" }}>
								<Col lg={10}>
									<Heading
										Tag="h3"
										sx={{
											fontSize: "m",
											fontWeight: "medium",
											fontColor: "white",
											textAlign: "center",
										}}
										spacing={{ mt: 10 }}
									>
										{t("ANALYZE_CATEGORICAL")}
									</Heading>
									<Paragraph sx={{ fontColor: "black--15", textAlign: "center" }}>
										{t("ANALYZE_CATEGORICAL_DESC")}
									</Paragraph>
								</Col>
							</Row>
							<Row utility={{ justify: "center" }}>
								<Col lg={10}>
									<Heading
										Tag="h3"
										sx={{
											fontSize: "m",
											fontWeight: "medium",
											fontColor: "white",
											textAlign: "center",
										}}
										spacing={{ mt: 10 }}
									>
										{t("INTERPOLATION_FROM_MULTIPLE")}
									</Heading>
									<Paragraph sx={{ fontColor: "black--15", textAlign: "center" }}>
										{t("INTERPOLATION_FROM_MULTIPLE_DESC")}
									</Paragraph>
								</Col>
							</Row>

							<Row>
								<Col sx={{ textAlign: "center" }} spacing={{ my: 10 }}>
									<Link
										sx={{
											fontSize: "xl",
											fontWeight: "medium",
											fontColor: "4",
											hover: { fontColor: "white" },
										}}
										href="/guides/prism/latest/user-guide/repeated-measures-anova-with-m.htm"
									>
										{t("READ_MORE")}
									</Link>
								</Col>
							</Row>
						</Col>
					</Row>
				</ContentContainer>
			</LandingSimpleSectionStripped>
			<SectionStart30DayTrial />
			<LandingSimpleSectionStripped>
				<LandingSimpleSection.TitleV2 spacing={{ mb: 20 }}>
					{t("DISCOVER_THE_BREADTH")}
				</LandingSimpleSection.TitleV2>
				<LandingSimpleSection.Body>
					<Row mx utility={{ justify: "center" }}>
						<Col md={6} spacing={{ px: 4 }}>
							<Heading Tag="h4" sx={{ fontSize: "m" }} spacing={{ my: 3 }}>
								{t("STATISTICAL_COMPARISONS")}
							</Heading>
							<UList>
								<UList.Item spacing={{ mb: 2, ml: 2 }}>
									{t("STATISTICAL_COMPARISONS_ITEM_1")}
								</UList.Item>
								<UList.Item spacing={{ mb: 2, ml: 2 }}>
									{t("STATISTICAL_COMPARISONS_ITEM_2")}
								</UList.Item>
								<UList.Item spacing={{ mb: 2, ml: 2 }}>
									{t("STATISTICAL_COMPARISONS_ITEM_3")}
								</UList.Item>
								<UList.Item spacing={{ mb: 2, ml: 2 }}>
									{t("STATISTICAL_COMPARISONS_ITEM_4")}
								</UList.Item>
								<UList.Item spacing={{ mb: 2, ml: 2 }}>
									{t("STATISTICAL_COMPARISONS_ITEM_5")}
								</UList.Item>
								<UList.Item spacing={{ mb: 2, ml: 2 }}>
									{t("STATISTICAL_COMPARISONS_ITEM_6")}
								</UList.Item>
								<UList.Item spacing={{ mb: 2, ml: 2 }}>
									{t("STATISTICAL_COMPARISONS_ITEM_7")}
								</UList.Item>
								<UList.Item spacing={{ mb: 2, ml: 2 }}>
									{t("STATISTICAL_COMPARISONS_ITEM_8")}
								</UList.Item>
								<UList.Item spacing={{ mb: 2, ml: 2 }}>
									{t("STATISTICAL_COMPARISONS_ITEM_9")}
								</UList.Item>
								<UList.Item spacing={{ mb: 2, ml: 2 }}>
									{t("STATISTICAL_COMPARISONS_ITEM_10")}
								</UList.Item>
								<UList.Item spacing={{ mb: 2, ml: 2 }}>
									<Html Tag="span">{t("STATISTICAL_COMPARISONS_ITEM_11")}</Html>
								</UList.Item>
								<UList.Item spacing={{ mb: 2, ml: 2 }}>
									<Html Tag="span">{t("STATISTICAL_COMPARISONS_ITEM_12")}</Html>
								</UList.Item>
								<UList.Item spacing={{ mb: 2, ml: 2 }}>
									{t("STATISTICAL_COMPARISONS_ITEM_13")}
								</UList.Item>
								<UList.Item spacing={{ mb: 2, ml: 2 }}>
									{t("STATISTICAL_COMPARISONS_ITEM_14")}
								</UList.Item>
								<UList.Item spacing={{ mb: 2, ml: 2 }}>
									{t("STATISTICAL_COMPARISONS_ITEM_15")}
								</UList.Item>
								<UList.Item spacing={{ mb: 2, ml: 2 }}>
									{t("STATISTICAL_COMPARISONS_ITEM_16")}
								</UList.Item>
								<UList.Item spacing={{ mb: 2, ml: 2 }}>
									{t("STATISTICAL_COMPARISONS_ITEM_17")}
								</UList.Item>
							</UList>
							<Heading Tag="h4" sx={{ fontSize: "m" }} spacing={{ my: 3 }}>
								{t("NONLINEAR_REGRESSION")}
							</Heading>
							<UList>
								<UList.Item spacing={{ mb: 2, ml: 2 }}>
									{t("NONLINEAR_REGRESSION_ITEM_1")}
								</UList.Item>
								<UList.Item spacing={{ mb: 2, ml: 2 }}>
									{t("NONLINEAR_REGRESSION_ITEM_2")}
								</UList.Item>
								<UList.Item spacing={{ mb: 2, ml: 2 }}>
									{t("NONLINEAR_REGRESSION_ITEM_3")}
								</UList.Item>
								<UList.Item spacing={{ mb: 2, ml: 2 }}>
									{t("NONLINEAR_REGRESSION_ITEM_4")}
								</UList.Item>
								<UList.Item spacing={{ mb: 2, ml: 2 }}>
									{t("NONLINEAR_REGRESSION_ITEM_5")}
								</UList.Item>
								<UList.Item spacing={{ mb: 2, ml: 2 }}>
									{t("NONLINEAR_REGRESSION_ITEM_6")}
								</UList.Item>
								<UList.Item spacing={{ mb: 2, ml: 2 }}>
									{t("NONLINEAR_REGRESSION_ITEM_7")}
								</UList.Item>
								<UList.Item spacing={{ mb: 2, ml: 2 }}>
									{t("NONLINEAR_REGRESSION_ITEM_8")}
								</UList.Item>
								<UList.Item spacing={{ mb: 2, ml: 2 }}>
									{t("NONLINEAR_REGRESSION_ITEM_9")}
								</UList.Item>
								<UList.Item spacing={{ mb: 2, ml: 2 }}>
									{t("NONLINEAR_REGRESSION_ITEM_10")}
								</UList.Item>
								<UList.Item spacing={{ mb: 2, ml: 2 }}>
									{t("NONLINEAR_REGRESSION_ITEM_11")}
								</UList.Item>
								<UList.Item spacing={{ mb: 2, ml: 2 }}>
									{t("NONLINEAR_REGRESSION_ITEM_12")}
								</UList.Item>
								<UList.Item spacing={{ mb: 2, ml: 2 }}>
									{t("NONLINEAR_REGRESSION_ITEM_13")}
								</UList.Item>
								<UList.Item spacing={{ mb: 2, ml: 2 }}>
									{t("NONLINEAR_REGRESSION_ITEM_14")}
								</UList.Item>
								<UList.Item spacing={{ mb: 2, ml: 2 }}>
									{t("NONLINEAR_REGRESSION_ITEM_15")}
								</UList.Item>
								<UList.Item spacing={{ mb: 2, ml: 2 }}>
									{t("NONLINEAR_REGRESSION_ITEM_16")}
								</UList.Item>
								<UList.Item spacing={{ mb: 2, ml: 2 }}>
									{t("NONLINEAR_REGRESSION_ITEM_17")}
								</UList.Item>
								<UList.Item spacing={{ mb: 2, ml: 2 }}>
									{t("NONLINEAR_REGRESSION_ITEM_18")}
								</UList.Item>
								<UList.Item spacing={{ mb: 2, ml: 2 }}>
									{t("NONLINEAR_REGRESSION_ITEM_19")}
								</UList.Item>
								<UList.Item spacing={{ mb: 2, ml: 2 }}>
									{t("NONLINEAR_REGRESSION_ITEM_20")}
								</UList.Item>
							</UList>
							<Heading Tag="h4" sx={{ fontSize: "m" }} spacing={{ my: 3 }}>
								{t("SURVIVAL_ANALYSIS")}
							</Heading>
							<UList>
								<UList.Item spacing={{ mb: 2, ml: 2 }}>{t("SURVIVAL_ANALYSIS_ITEM_1")}</UList.Item>
								<UList.Item spacing={{ mb: 2, ml: 2 }}>{t("SURVIVAL_ANALYSIS_ITEM_2")}</UList.Item>
							</UList>
						</Col>
						<Col md={6} spacing={{ px: 4 }}>
							<Heading Tag="h4" sx={{ fontSize: "m" }} spacing={{ my: 3 }}>
								{t("PRINCIPAL_COMPONENT_ANALYSIS_TITLE")}
							</Heading>

							<UList>
								<UList.Item spacing={{ mb: 2, ml: 2 }}>
									{t("PRINCIPAL_COMPONENT_ANALYSIS_ITEM_1")}
								</UList.Item>
								<UList.Item spacing={{ mb: 2, ml: 2 }}>
									{t("PRINCIPAL_COMPONENT_ANALYSIS_ITEM_2")}
								</UList.Item>
								<UList.Item spacing={{ mb: 2, ml: 2 }}>
									{t("PRINCIPAL_COMPONENT_ANALYSIS_ITEM_3")}
								</UList.Item>
							</UList>
							<Heading Tag="h4" sx={{ fontSize: "m" }} spacing={{ my: 3 }}>
								{t("MULTIPLE_VARIABLE_GRAPHING")}
							</Heading>
							<UList>
								<UList.Item spacing={{ mb: 2, ml: 2 }}>
									{t("MULTIPLE_VARIABLE_GRAPHING_ITEM_1")}
								</UList.Item>
								<UList.Item spacing={{ mb: 2, ml: 2 }}>
									{t("MULTIPLE_VARIABLE_GRAPHING_ITEM_2")}
								</UList.Item>
							</UList>
							<Heading Tag="h4" sx={{ fontSize: "m" }} spacing={{ my: 3 }}>
								{t("COLUMN_STATISTICS")}
							</Heading>

							<UList>
								<UList.Item spacing={{ mb: 2, ml: 2 }}>{t("COLUMN_STATISTICS_ITEM_1")}</UList.Item>
								<UList.Item spacing={{ mb: 2, ml: 2 }}>{t("COLUMN_STATISTICS_ITEM_2")}</UList.Item>
								<UList.Item spacing={{ mb: 2, ml: 2 }}>{t("COLUMN_STATISTICS_ITEM_3")}</UList.Item>
								<UList.Item spacing={{ mb: 2, ml: 2 }}>{t("COLUMN_STATISTICS_ITEM_4")}</UList.Item>
								<UList.Item spacing={{ mb: 2, ml: 2 }}>{t("COLUMN_STATISTICS_ITEM_5")}</UList.Item>
								<UList.Item spacing={{ mb: 2, ml: 2 }}>{t("COLUMN_STATISTICS_ITEM_6")}</UList.Item>
								<UList.Item spacing={{ mb: 2, ml: 2 }}>{t("COLUMN_STATISTICS_ITEM_7")}</UList.Item>
								<UList.Item spacing={{ mb: 2, ml: 2 }}>{t("COLUMN_STATISTICS_ITEM_8")}</UList.Item>
								<UList.Item spacing={{ mb: 2, ml: 2 }}>
									<Html Tag="span">{t("COLUMN_STATISTICS_ITEM_9")}</Html>
								</UList.Item>
							</UList>
							<Heading Tag="h4" sx={{ fontSize: "m" }} spacing={{ my: 3 }}>
								{t("SIMPLE_LINEAR")}
							</Heading>
							<UList>
								<UList.Item spacing={{ mb: 2, ml: 2 }}>{t("SIMPLE_LINEAR_ITEM_1")}</UList.Item>
								<UList.Item spacing={{ mb: 2, ml: 2 }}>{t("SIMPLE_LINEAR_ITEM_2")}</UList.Item>
								<UList.Item spacing={{ mb: 2, ml: 2 }}>{t("SIMPLE_LINEAR_ITEM_3")}</UList.Item>
								<UList.Item spacing={{ mb: 2, ml: 2 }}>{t("SIMPLE_LINEAR_ITEM_4")}</UList.Item>
								<UList.Item spacing={{ mb: 2, ml: 2 }}>{t("SIMPLE_LINEAR_ITEM_5")}</UList.Item>
								<UList.Item spacing={{ mb: 2, ml: 2 }}>{t("SIMPLE_LINEAR_ITEM_6")}</UList.Item>
								<UList.Item spacing={{ mb: 2, ml: 2 }}>{t("SIMPLE_LINEAR_ITEM_7")}</UList.Item>
								<UList.Item spacing={{ mb: 2, ml: 2 }}>{t("SIMPLE_LINEAR_ITEM_8")}</UList.Item>
							</UList>
							<Heading Tag="h4" sx={{ fontSize: "m" }} spacing={{ my: 3 }}>
								{t("GENERALIZED_LINEAR_MODELS")}
							</Heading>
							<UList>
								<UList.Item spacing={{ mb: 2, ml: 2 }}>
									{t("GENERALIZED_LINEAR_MODELS_ITEM_1")}
								</UList.Item>
								<UList.Item spacing={{ mb: 2, ml: 2 }}>
									{t("GENERALIZED_LINEAR_MODELS_ITEM_2")}
								</UList.Item>
								<UList.Item spacing={{ mb: 2, ml: 2 }}>
									{t("GENERALIZED_LINEAR_MODELS_ITEM_3")}
								</UList.Item>
								<UList.Item spacing={{ mb: 2, ml: 2 }}>
									{t("GENERALIZED_LINEAR_MODELS_ITEM_4")}
								</UList.Item>
							</UList>
							<Heading Tag="h4" sx={{ fontSize: "m" }} spacing={{ my: 3 }}>
								{t("CLINICAL_LAB_STATISTICS")}
							</Heading>

							<UList>
								<UList.Item spacing={{ mb: 2, ml: 2 }}>
									{t("CLINICAL_LAB_STATISTICS_ITEM_1")}
								</UList.Item>
								<UList.Item spacing={{ mb: 2, ml: 2 }}>
									{t("CLINICAL_LAB_STATISTICS_ITEM_2")}
								</UList.Item>
								<UList.Item spacing={{ mb: 2, ml: 2 }}>
									{t("CLINICAL_LAB_STATISTICS_ITEM_3")}
								</UList.Item>
							</UList>
							<Heading Tag="h4" sx={{ fontSize: "m" }} spacing={{ my: 3 }}>
								{t("SIMULATIONS")}
							</Heading>

							<UList>
								<UList.Item spacing={{ mb: 2, ml: 2 }}>{t("SIMULATIONS_ITEM_1")}</UList.Item>
								<UList.Item spacing={{ mb: 2, ml: 2 }}>{t("SIMULATIONS_ITEM_2")}</UList.Item>
								<UList.Item spacing={{ mb: 2, ml: 2 }}>{t("SIMULATIONS_ITEM_3")}</UList.Item>
							</UList>
							<Heading Tag="h4" sx={{ fontSize: "m" }} spacing={{ my: 3 }}>
								{t("OTHER_CALCULATIONS")}
							</Heading>

							<UList>
								<UList.Item spacing={{ mb: 2, ml: 2 }}>{t("OTHER_CALCULATIONS_ITEM_1")}</UList.Item>
								<UList.Item spacing={{ mb: 2, ml: 2 }}>{t("OTHER_CALCULATIONS_ITEM_2")}</UList.Item>
								<UList.Item spacing={{ mb: 2, ml: 2 }}>{t("OTHER_CALCULATIONS_ITEM_3")}</UList.Item>
								<UList.Item spacing={{ mb: 2, ml: 2 }}>{t("OTHER_CALCULATIONS_ITEM_4")}</UList.Item>
								<UList.Item spacing={{ mb: 2, ml: 2 }}>{t("OTHER_CALCULATIONS_ITEM_5")}</UList.Item>
								<UList.Item spacing={{ mb: 2, ml: 2 }}>{t("OTHER_CALCULATIONS_ITEM_6")}</UList.Item>
								<UList.Item spacing={{ mb: 2, ml: 2 }}>{t("OTHER_CALCULATIONS_ITEM_7")}</UList.Item>
								<UList.Item spacing={{ mb: 2, ml: 2 }}>{t("OTHER_CALCULATIONS_ITEM_8")}</UList.Item>
							</UList>
						</Col>
					</Row>
				</LandingSimpleSection.Body>
			</LandingSimpleSectionStripped>
			<AvailableOnOs />
		</SiteLayout>
	);
};

export default pipe(PrismAnova, withErrorHandling, WithServices);
